import React from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import type { MapPin } from './MapView';
import type { MapReferencePoint } from '@/types/mapReferences';
import type { ItineraryItem } from '@/services/itinerary';
import { formatPlaceCategoryLabel, type Place } from '@/services/places';

type PinQuickPreviewProps = {
  pin: MapPin;
  visible: boolean;
  onClose: () => void;
  onViewDetails: () => void;
  onGetDirections?: (pin: MapPin) => void;
  onAddToItinerary?: (pin: MapPin) => void;
  primaryReferencePoint?: MapReferencePoint;
  distance?: number;
};

export const PinQuickPreview: React.FC<PinQuickPreviewProps> = ({
  pin,
  visible,
  onClose,
  onViewDetails,
  onGetDirections,
  onAddToItinerary,
  primaryReferencePoint,
  distance,
}) => {
  const theme = useTheme();

  if (!visible) return null;

  const formatStartTime = (time?: string): string | null => {
    if (!time || time === 'flexible') return time === 'flexible' ? 'Flexible' : null;
    const [hours, minutes] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const itineraryItem =
    pin.type === 'itinerary' ? (pin.data as ItineraryItem | undefined) : undefined;
  const startTimeLabel = formatStartTime(itineraryItem?.startTime);

  const getPinIcon = () => {
    switch (pin.type) {
      case 'itinerary':
        return 'calendar';
      case 'landmark':
        return 'star';
      case 'user':
        return 'person';
      case 'search':
        return 'location';
      default:
        return 'pin';
    }
  };

  const getPinTypeLabel = () => {
    switch (pin.type) {
      case 'itinerary':
        return 'Itinerary Item';
      case 'landmark':
        return formatPlaceCategoryLabel((pin.data as Place | undefined)?.category);
      case 'user':
        return 'Your Location';
      case 'search':
        return 'Place';
      default:
        return 'Location';
    }
  };

  return (
    <Pressable
      style={styles.backdrop}
      onPress={onClose}
    >
      <Pressable onPress={(e) => e.stopPropagation()}>
        <Card
          style={[
            styles.previewCard,
            {
              backgroundColor: theme.colors.surface,
              ...(Platform.OS === 'web' ? {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              } : {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.25,
                shadowRadius: 8,
                elevation: 8,
              }),
            },
          ]}
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '15' }]}>
              <Ionicons
                name={getPinIcon()}
                size={24}
                color={theme.colors.primary}
              />
            </View>
            <View style={styles.headerText}>
              <Body style={[styles.title, { color: theme.colors.text }]}>
                {pin.title}
              </Body>
              <Caption color={theme.colors.textSecondary} style={styles.typeLabel}>
                {getPinTypeLabel()}
              </Caption>
            </View>
            <Pressable
              onPress={onClose}
              style={styles.closeButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={20} color={theme.colors.textSecondary} />
            </Pressable>
          </View>

          {/* Subtitle */}
          {pin.subtitle && (
            <Caption color={theme.colors.textSecondary} style={styles.subtitle}>
              {pin.subtitle}
            </Caption>
          )}

          {/* Start time (itinerary pins) */}
          {pin.type === 'itinerary' && startTimeLabel && (
            <View style={styles.timeRow}>
              <Ionicons name="time-outline" size={16} color={theme.colors.primary} />
              <Caption
                color={theme.colors.textSecondary}
                style={styles.timeText}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {startTimeLabel}
              </Caption>
            </View>
          )}

          {/* Distance */}
          {distance !== undefined && primaryReferencePoint && (
            <View style={styles.distanceContainer}>
              <Ionicons name="navigate-outline" size={16} color={theme.colors.primary} />
              <Caption color={theme.colors.textSecondary} style={styles.distance}>
                {distance < 1
                  ? `${Math.round(distance * 1000)}m from ${primaryReferencePoint.title}`
                  : `${distance.toFixed(1)}km from ${primaryReferencePoint.title}`}
              </Caption>
            </View>
          )}

          {/* Badges */}
          {pin.badges && pin.badges.length > 0 && (
            <View style={styles.badgesContainer}>
              {pin.badges.map((badge, index) => (
                <View
                  key={index}
                  style={[
                    styles.badge,
                    {
                      backgroundColor: theme.colors.primary + '15',
                    },
                  ]}
                >
                  <Caption style={[styles.badgeText, { color: theme.colors.primary }]}>
                    {badge}
                  </Caption>
                </View>
              ))}
            </View>
          )}

          {/* Quick Actions */}
          <View style={styles.actionsContainer}>
            {onGetDirections && (
              <Pressable
                onPress={() => {
                  onGetDirections(pin);
                  onClose();
                }}
                style={({ pressed }) => [
                  styles.actionButton,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Ionicons name="navigate-outline" size={18} color={theme.colors.primary} />
                <Body style={[styles.actionLabel, { color: theme.colors.primary }]}>
                  Directions
                </Body>
              </Pressable>
            )}
            {onAddToItinerary && (pin.type === 'landmark' || pin.type === 'search') && (
              <Pressable
                onPress={() => {
                  onAddToItinerary(pin);
                  onClose();
                }}
                style={({ pressed }) => [
                  styles.actionButton,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Ionicons name="add-circle-outline" size={18} color={theme.colors.primary} />
                <Body style={[styles.actionLabel, { color: theme.colors.primary }]}>
                  Add to Trip
                </Body>
              </Pressable>
            )}
            <Pressable
              onPress={() => {
                onViewDetails();
              }}
              style={({ pressed }) => [
                styles.actionButton,
                styles.primaryAction,
                {
                  backgroundColor: theme.colors.primary,
                  opacity: pressed ? 0.8 : 1,
                },
              ]}
            >
              <Body style={[styles.actionLabel, { color: theme.colors.surface }]}>
                View Details
              </Body>
              <Ionicons name="chevron-forward" size={18} color={theme.colors.surface} />
            </Pressable>
          </View>
        </Card>
      </Pressable>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
    zIndex: 1001,
  },
  previewCard: {
    margin: 16,
    marginBottom: Platform.OS === 'web' ? 16 : 32,
    padding: 16,
    borderRadius: 16,
    maxWidth: Platform.OS === 'web' ? 400 : undefined,
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontWeight: '600',
    fontSize: 16,
  },
  typeLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  closeButton: {
    padding: 4,
    flexShrink: 0,
  },
  subtitle: {
    marginBottom: 12,
    fontSize: 13,
    lineHeight: 18,
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
  },
  timeText: {
    flexShrink: 1,
  },
  distanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
  },
  distance: {
    fontSize: 12,
  },
  badgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 16,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  actionsContainer: {
    gap: 8,
    marginTop: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  primaryAction: {
    borderWidth: 0,
  },
  actionLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
});

